import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm, DATE_FORMATS_FNS, useCan } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { CHECKLIST_TYPES, COMMUNICATION_EVENTS, STATUS_IDENTIFIERS } from "models";
import { CarMileageModal, DeskCommunication, StatusBarIcon, isPonOcwAvailable } from "modules/AppointmentDetails/components";
import "modules/AppointmentDetails/components/AppointmentStatus/AppointmentStatus.scss";
import {
  useSetCarReadyStatusMutation,
  useSetContactStatusMutation,
  useSetCustomerOkStatusMutation,
  useSetMakeAnOfferStatusMutation,
  useSetPricingOkStatusMutation
} from "modules/AppointmentDetails/components/AppointmentStatus/hooks";
import { useGetOCWVehicleStatus } from "modules/AppointmentDetails/components/OCW/hooks";
import {
  useAppointmentCarInShopMutation,
  useAppointmentCarOutOfShopMutation,
  useAppointmentDealer,
  useAppointmentLocation,
  useCarBrandsQuery,
  useDetachKioskLabelMutation,
  useRealTimeOrderedChecks,
  useRestoreAppointmentMutation
} from "modules/AppointmentDetails/hooks";
import { isCarReady, isCarReadyOrQualityCheck, isQualityCheck, isWorkNotStarted } from "utils/appointmentUtils";
import { isKeyLoopLocation } from "utils/common";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { Appointment, CarCheck, Checklist, CommunicationEvent, DealerLocation, InterventionPendingChecklist } from "models";

const ONLINE_CHECK_IN_EVENTS = [COMMUNICATION_EVENTS.OnlineCheckInSent, COMMUNICATION_EVENTS.OnlineCheckInOpened, COMMUNICATION_EVENTS.OnlineCheckInAnswered];
const DIAGNOSE_OVERVIEW_EVENTS = [
  COMMUNICATION_EVENTS.DiagnoseOverviewSent,
  COMMUNICATION_EVENTS.DiagnoseOverviewOpened,
  COMMUNICATION_EVENTS.DiagnoseOverviewResent,
  COMMUNICATION_EVENTS.DiagnoseOverviewAnswered
];
const REPAIR_OVERVIEW_EVENTS = [COMMUNICATION_EVENTS.RepairOverviewSent, COMMUNICATION_EVENTS.RepairOverviewResent, COMMUNICATION_EVENTS.RepairOverviewOpened];

type AppointmentStatusProps = {
  appointment: Appointment;
  checklists?: Checklist[];
};

type StatusIconProps = {
  status: STATUS_IDENTIFIERS;
  active?: boolean;
  name?: string;
  timestamp?: string | null;
  disabled?: boolean;
  color?: string;
  isMechanicState?: boolean;
  checkPausedAt?: string | null;
};

type QualityCheckOrCarReadyStatusProps = {
  appointment: Appointment;
  onClick?: () => void;
  isUpdating?: boolean;
  canSetCarReady: boolean;
  selectedLocation?: DealerLocation;
  hasPendingChecklistError: () => boolean;
  communicationEvents?: CommunicationEvent[];
};

const getLatestCommunicationEvents = (events: CommunicationEvent[]) => {
  const diagnoseOverviewEvents = events.filter(event => DIAGNOSE_OVERVIEW_EVENTS.includes(event.type));
  if (diagnoseOverviewEvents.length) return diagnoseOverviewEvents;

  return events.filter(event => ONLINE_CHECK_IN_EVENTS.includes(event.type));
};

const getCustomerAnsweredName = (events?: CommunicationEvent[]) => {
  const answeredIndex = events?.findLastIndex(event => event.type === COMMUNICATION_EVENTS.DiagnoseOverviewAnswered) ?? -1;
  if (answeredIndex === -1) return "";

  const answeredEvent = events?.[answeredIndex];
  if (answeredEvent?.customer_name) return answeredEvent.customer_name;

  const customer = events?.findLast((event, index) => index <= answeredIndex && event.customer)?.customer;
  if (!customer) return "";

  return `${customer.firstname} ${customer.surname ?? ""}`.trim();
};

const getAppointmentStatusNameAndTime = (status: STATUS_IDENTIFIERS, appointment: Appointment | null) => {
  const statusHistory = appointment?.status_history?.findLast(history => status === history.identifier);
  if (!statusHistory) return { name: "", timestamp: "" };

  if (statusHistory.user_id === 0 && statusHistory.identifier === STATUS_IDENTIFIERS.CustomerAnsweredStatus)
    return { name: getCustomerAnsweredName(appointment?.customer_communication?.events), timestamp: statusHistory.created_on };

  return { name: statusHistory.last_user, timestamp: statusHistory.created_on };
};

const getIntermittentStatus = (appointment: Appointment, checks: CarCheck[] | undefined, communicationEvents: CommunicationEvent[]) => {
  const { name, timestamp } = getAppointmentStatusNameAndTime(appointment.appointment_status_identifier, appointment);

  let statusIconProps: StatusIconProps = {
    name,
    timestamp,
    disabled: true,
    status: STATUS_IDENTIFIERS.CarCheckStatus
  };

  const noQualityChecklistOnAppointment = !checks?.some(check => check.checklist?.checklist_type === CHECKLIST_TYPES.Quality);

  switch (appointment.appointment_status_identifier) {
    case STATUS_IDENTIFIERS.NewCarStatus:
      statusIconProps = { ...statusIconProps, status: STATUS_IDENTIFIERS.CarCheckStatus, name: "", timestamp: null, isMechanicState: true };
      break;
    case STATUS_IDENTIFIERS.CarCheckStartedStatus: {
      if (noQualityChecklistOnAppointment)
        statusIconProps = { ...statusIconProps, status: STATUS_IDENTIFIERS.CarCheckStartedStatus, checkPausedAt: appointment.car_check_paused_at, active: true };

      break;
    }
    case STATUS_IDENTIFIERS.CarCheckStatus:
      statusIconProps = { ...statusIconProps, status: STATUS_IDENTIFIERS.CarCheckStatus, active: true };
      break;
    case STATUS_IDENTIFIERS.AannameOKStatus:
      statusIconProps = { ...statusIconProps, status: STATUS_IDENTIFIERS.AannameOKStatus, active: true };
      break;
    case STATUS_IDENTIFIERS.DiagnoseStatus:
      statusIconProps = { ...statusIconProps, status: STATUS_IDENTIFIERS.DiagnoseStatus, active: true };
      break;
    default: {
      const lastStatus = appointment.status_history?.findLast(
        status =>
          [STATUS_IDENTIFIERS.CarCheckStartedStatus, STATUS_IDENTIFIERS.CarCheckStatus, STATUS_IDENTIFIERS.AannameOKStatus, STATUS_IDENTIFIERS.DiagnoseStatus].includes(
            status.identifier
          ) && noQualityChecklistOnAppointment
      );
      if (lastStatus)
        statusIconProps = {
          ...statusIconProps,
          status: lastStatus.identifier,
          name: lastStatus.last_user,
          timestamp: lastStatus.created_on,
          isMechanicState: true,
          active: false
        };
      else {
        statusIconProps = {
          ...statusIconProps,
          name: "",
          timestamp: null
        };
      }
    }
  }

  return <StatusBarIcon {...statusIconProps} communicationEvents={communicationEvents} />;
};

const QualityCheckOrCarReadyStatus = ({
  appointment,
  isUpdating,
  onClick,
  canSetCarReady,
  selectedLocation,
  hasPendingChecklistError,
  communicationEvents
}: QualityCheckOrCarReadyStatusProps) => {
  const lastQualityCheckStatus = appointment.car_check_events?.findLast(ce =>
    appointment.car_checks?.some(check => ce.car_check_id === check.id && check.checklist?.checklist_type === CHECKLIST_TYPES.Quality)
  );

  if (isQualityCheck(appointment.appointment_status_identifier)) {
    const { name, timestamp } = getAppointmentStatusNameAndTime(appointment.appointment_status_identifier, appointment);

    return (
      <StatusBarIcon status={appointment.appointment_status_identifier} name={name} timestamp={timestamp} communicationEvents={communicationEvents} active disabled />
    );
  }

  if (appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CarCheckStartedStatus && lastQualityCheckStatus) {
    return (
      <StatusBarIcon
        status={STATUS_IDENTIFIERS.QualityCheckStatus}
        name={lastQualityCheckStatus.user_full_name || ""}
        timestamp={lastQualityCheckStatus.created_on}
        communicationEvents={communicationEvents}
        active
        disabled
      />
    );
  }

  const isCarReadyStatusDisabled =
    !canSetCarReady ||
    !selectedLocation?.car_ready_btn_visible ||
    appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CanceledStatus ||
    isUpdating ||
    (appointment.appointment_status_identifier !== STATUS_IDENTIFIERS.BackOrder && isCarReady(appointment.appointment_status_identifier));
  const { name, timestamp } = getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CarReadyStatus, appointment);

  if (isCarReady(appointment.appointment_status_identifier) || selectedLocation?.car_ready_btn_visible) {
    return (
      <StatusBarIcon
        name={name}
        timestamp={timestamp}
        status={STATUS_IDENTIFIERS.CarReadyStatus}
        communicationEvents={communicationEvents}
        active={isCarReady(appointment.appointment_status_identifier)}
        disabled={isCarReadyStatusDisabled}
        onClick={() => {
          if (hasPendingChecklistError()) return;
          onClick?.();
        }}
      />
    );
  }
};

export const AppointmentStatus = ({ appointment, checklists }: AppointmentStatusProps) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDetachKioskModel, setShowDetachKioskModel] = useState(false);
  const [currentMileage, setCurrentMileage] = useState<number | null>(null);
  const [showCarMileageModal, setShowCarMileageModal] = useState(false);
  const [showDeskCommunicationModal, setShowDeskCommunicationModal] = useState(false);

  const { t } = useTranslation();
  const { user } = usePermanentRealTimeUser();
  const selectedLocation = useAppointmentLocation();
  const appointmentDealer = useAppointmentDealer(appointment);
  const restoreAppointmentMutation = useRestoreAppointmentMutation(appointment.id);
  const detachKioskMutation = useDetachKioskLabelMutation(appointment.id);
  const canRestoreAppointments = useCan("create", "appointments-new-car");
  const canUpdateCarInShop = useCan("update", "appointments-car-in-shop");
  const canUpdateCarOutOfShop = useCan("update", "appointments-car-out-of-shop");
  const canSetMakeAnOffer = useCan("create", "appointments-make-an-offer");
  const canSetPricingOk = useCan("create", "appointments-pricing-ok");
  const canSetContact = useCan("create", "appointments-contact");
  const canSetCustomerOk = useCan("create", "appointments-customer-ok");
  const canSetCarReady = useCan("create", "appointments-car-ready");
  const { mutate: updateCarInShop } = useAppointmentCarInShopMutation(appointment.id);
  const { mutate: updateCarOutOfShop } = useAppointmentCarOutOfShopMutation(appointment.id);
  const { checks } = useRealTimeOrderedChecks(appointment.id);
  const setCarReadyStatus = useSetCarReadyStatusMutation(appointment.id);
  const setContactStatus = useSetContactStatusMutation(appointment.id);
  const setCustomerOkStatus = useSetCustomerOkStatusMutation(appointment.id);
  const setMakeAnOfferStatus = useSetMakeAnOfferStatusMutation(appointment.id);
  const setPricingOkStatus = useSetPricingOkStatusMutation(appointment.id);
  const { data: carBrands = [] } = useCarBrandsQuery();

  const appointmentStatus: STATUS_IDENTIFIERS | undefined = appointment.appointment_status_identifier;
  const events = appointment.customer_communication?.events?.toSorted((a, b) => a.id - b.id) ?? [];
  const communicationEvents = getLatestCommunicationEvents(events);
  const repairOverviewEvents = events.filter(event => REPAIR_OVERVIEW_EVENTS.includes(event.type));
  const carInShopStatus = appointment.car_in_shop !== null && appointment.car_out_of_shop === null;
  const carInShopDisabled = appointmentStatus === STATUS_IDENTIFIERS.NewCarStatus ? !canUpdateCarInShop : !canUpdateCarOutOfShop;
  const isUpdating =
    setCarReadyStatus.isPending || setContactStatus.isPending || setCustomerOkStatus.isPending || setMakeAnOfferStatus.isPending || setPricingOkStatus.isPending;
  const { data: ocwVehicleStatusData } = useGetOCWVehicleStatus(
    { appointment_id: appointment.id },
    isPonOcwAvailable(carBrands, appointment.vin_nr, selectedLocation, appointmentDealer)
  );

  const getCarReadyConfirmationMessage = () => {
    if (ocwVehicleStatusData?.workshop_mode) return t("v8_car_ok_status_maintenace_mode", "Maintenance mode is active. Do you still want to set the Car ready?");

    return t("v8_are_you_sure_car_ok_status", "Are you sure you want to change the status to Car Ready?");
  };

  const allPendingChecklists: InterventionPendingChecklist[] = appointment.interventions?.flatMap(intervention => intervention.pending_checklists ?? []) ?? [];

  const hasPendingChecklistError = () => {
    if (allPendingChecklists.length === 0) return false;

    const hasPendingChecklistThatUpdatesStatus = allPendingChecklists.some(pc => {
      const template = checklists?.find(c => c.id === pc.checklist_id)?.template;

      if (!template?.skip_update_appointment_status) return true;

      return false;
    });

    if (hasPendingChecklistThatUpdatesStatus) {
      const interventionName = appointment.interventions?.find(intervention => intervention.id === allPendingChecklists[0]?.intervention_id)?.title || "";
      toast.error(
        `${t("v8_intervention", "Intervention")} ${interventionName} ${t("v8_has_pending_checklists_to_be_completed_first", "has pending checklist(s) to be completed first")}`
      );
      return true;
    }

    return false;
  };

  const toggleConfirmModal = () => {
    setShowConfirmModal(prev => !prev);
  };

  const handleConfirm = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setCarReadyStatus.mutate();
    toggleConfirmModal();
  };

  const isActive = (appointmentStatusId: STATUS_IDENTIFIERS | undefined, statusId: number) => {
    return appointmentStatusId === statusId;
  };

  const handleDetachKiosk = () => {
    detachKioskMutation.mutate();
    setShowDetachKioskModel(false);
  };

  if (appointmentStatus === STATUS_IDENTIFIERS.CanceledStatus && !canRestoreAppointments) return null;

  const carInShopName = () => {
    if (carInShopStatus) return appointment.car_in_shop_set_by ? `${appointment.car_in_shop_set_by.first_name} ${appointment.car_in_shop_set_by.last_name}`.trim() : "";
    else return (appointment.car_out_of_shop_set_by && `${appointment.car_out_of_shop_set_by.first_name} ${appointment.car_out_of_shop_set_by.last_name}`.trim()) || "";
  };

  const onAppointmentStatusHandler = () => {
    if (isUpdating) return;

    if (appointmentStatus === STATUS_IDENTIFIERS.CanceledStatus) {
      restoreAppointmentMutation.mutate();
      return;
    }

    const timeStamp = formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormatWithZ);
    if (carInShopStatus) {
      if (!isKeyLoopLocation(selectedLocation) || appointment.current_km) {
        if (selectedLocation?.is_open_desk_checkout_on_car_out_of_shop_enabled && user?.counter_tablet_key && isCarReadyOrQualityCheck(appointmentStatus))
          return setShowDeskCommunicationModal(true);

        updateCarOutOfShop({ car_out_of_shop: timeStamp });
        if (appointment.kiosk_label_number) setShowDetachKioskModel(true);

        return;
      }

      if (!appointment.current_km && !currentMileage) return setShowCarMileageModal(true);
      if (selectedLocation?.is_open_desk_checkout_on_car_out_of_shop_enabled && user?.counter_tablet_key && isCarReadyOrQualityCheck(appointmentStatus))
        return setShowDeskCommunicationModal(true);
      if (appointment.kiosk_label_number) setShowDetachKioskModel(true);
      updateCarOutOfShop({ car_out_of_shop: timeStamp, ...(currentMileage && { current_km: currentMileage }) });
    } else {
      if (!isKeyLoopLocation(selectedLocation) || appointment.current_km) return updateCarInShop({ car_in_shop: timeStamp });
      if (!appointment.current_km && !currentMileage) return setShowCarMileageModal(true);
      updateCarInShop({ car_in_shop: timeStamp, ...(currentMileage && { current_km: currentMileage }) });
    }
  };

  const resetCarMileageModal = () => {
    setShowCarMileageModal(false);
    setCurrentMileage(null);
  };

  const handleCarMileageConfirm = () => {
    if (currentMileage) {
      if (appointment.next_km && currentMileage > appointment.next_km) {
        return toast.error(t("v8_current_mileage_greater_then_next_km ", "Current mileage cannot be greater than next km"));
      } else if (appointment.dms_km && currentMileage <= appointment.dms_km) {
        return toast.error(t("v8_current_mileage_less_then_dms_km", "Current mileage cannot be less than dms km"));
      }
    }
    resetCarMileageModal();
    onAppointmentStatusHandler();
  };

  return (
    <div className="AppointmentStatus">
      <StatusBarIcon
        name={carInShopName()}
        timestamp={carInShopStatus ? appointment.car_in_shop : appointment.car_out_of_shop}
        status={carInShopStatus ? STATUS_IDENTIFIERS.CarInShop : STATUS_IDENTIFIERS.CarOutOfShop}
        active={carInShopStatus}
        disabled={carInShopDisabled}
        communicationEvents={isWorkNotStarted(appointmentStatus) ? communicationEvents : undefined}
        onClick={onAppointmentStatusHandler}
      />

      {getIntermittentStatus(appointment, checks, communicationEvents)}

      <StatusBarIcon
        name={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CalculatingStatus, appointment).name}
        timestamp={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CalculatingStatus, appointment).timestamp}
        status={STATUS_IDENTIFIERS.CalculatingStatus}
        disabled={appointmentStatus !== STATUS_IDENTIFIERS.CarCheckStatus || !canSetMakeAnOffer}
        active={isActive(appointmentStatus, STATUS_IDENTIFIERS.CalculatingStatus)}
        communicationEvents={communicationEvents}
        onClick={() => {
          if (hasPendingChecklistError()) return;
          !setMakeAnOfferStatus.isPending && setMakeAnOfferStatus.mutate();
        }}
      />

      <StatusBarIcon
        name={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.PricingOKStatus, appointment).name}
        timestamp={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.PricingOKStatus, appointment).timestamp}
        status={STATUS_IDENTIFIERS.PricingOKStatus}
        disabled={appointmentStatus !== STATUS_IDENTIFIERS.CalculatingStatus || !canSetPricingOk}
        active={isActive(appointmentStatus, STATUS_IDENTIFIERS.PricingOKStatus)}
        communicationEvents={communicationEvents}
        onClick={() => {
          if (hasPendingChecklistError()) return;
          !setPricingOkStatus.isPending && setPricingOkStatus.mutate();
        }}
      />

      <StatusBarIcon
        name={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.ContactStatus, appointment).name}
        timestamp={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.ContactStatus, appointment).timestamp}
        status={STATUS_IDENTIFIERS.ContactStatus}
        customcomStatus={appointment.customer_communication?.status}
        disabled={appointmentStatus !== STATUS_IDENTIFIERS.PricingOKStatus || !canSetContact}
        active={isActive(appointmentStatus, STATUS_IDENTIFIERS.ContactStatus)}
        communicationEvents={communicationEvents}
        onClick={() => {
          if (hasPendingChecklistError()) return;
          !setContactStatus.isPending && setContactStatus.mutate();
        }}
      />

      {selectedLocation?.diagnose_overview_enabled && (
        <StatusBarIcon
          status={STATUS_IDENTIFIERS.CustomerAnsweredStatus}
          name={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CustomerAnsweredStatus, appointment).name}
          timestamp={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CustomerAnsweredStatus, appointment).timestamp}
          disabled
          active={appointmentStatus === STATUS_IDENTIFIERS.CustomerAnsweredStatus}
          communicationEvents={communicationEvents}
        />
      )}

      {appointmentStatus === STATUS_IDENTIFIERS.BackOrder && (
        <StatusBarIcon
          name={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.BackOrder, appointment).name}
          timestamp={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.BackOrder, appointment).timestamp}
          status={STATUS_IDENTIFIERS.BackOrder}
          active={true}
          communicationEvents={communicationEvents}
        />
      )}

      <StatusBarIcon
        name={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CustomerOKStatus, appointment).name}
        timestamp={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CustomerOKStatus, appointment).timestamp}
        status={STATUS_IDENTIFIERS.CustomerOKStatus}
        active={isActive(appointmentStatus, STATUS_IDENTIFIERS.CustomerOKStatus)}
        communicationEvents={communicationEvents}
        disabled={
          !canSetCustomerOk ||
          appointmentStatus === STATUS_IDENTIFIERS.CanceledStatus ||
          (appointmentStatus !== STATUS_IDENTIFIERS.ContactStatus &&
            appointmentStatus !== STATUS_IDENTIFIERS.PricingOKStatus &&
            appointmentStatus !== STATUS_IDENTIFIERS.CustomerAnsweredStatus &&
            appointmentStatus !== STATUS_IDENTIFIERS.BackOrder)
        }
        onClick={() => {
          if (hasPendingChecklistError()) return;
          !setCustomerOkStatus.isPending && setCustomerOkStatus.mutate();
        }}
      />

      <QualityCheckOrCarReadyStatus
        isUpdating={isUpdating}
        appointment={appointment}
        onClick={toggleConfirmModal}
        canSetCarReady={canSetCarReady}
        selectedLocation={selectedLocation}
        hasPendingChecklistError={hasPendingChecklistError}
        communicationEvents={repairOverviewEvents}
      />

      <div className="horizontal-line" />
      <CustomConfirm
        isLoading={!!isUpdating}
        isOpen={showConfirmModal}
        type={CUSTOM_CONFIRM_TYPES.Warning}
        handleConfirm={handleConfirm}
        handleCancel={toggleConfirmModal}
        confirmMsg={getCarReadyConfirmationMessage()}
      />
      <CustomConfirm
        customTitle={t("v8_key_label_warning", "Key Label Warning")}
        type={CUSTOM_CONFIRM_TYPES.Warning}
        allowCustomReason
        isOpen={showDetachKioskModel}
        customClass={"CancelAppointmentModal"}
        handleCancel={() => setShowDetachKioskModel(false)}
        handleConfirm={handleDetachKiosk}
        isLoading={detachKioskMutation.isPending}
        confirmMsg={t("v8_confirm_disconnect_key_from_appointment", "Do you want to disconnect the key label from this WO?")}
        cancelButtonText={t("v8_no", "No")}
        confirmButtonText={t("v8_yes", "Yes")}
      />

      <CarMileageModal
        showCarMileageModal={showCarMileageModal}
        currentMileage={currentMileage}
        onSetCurrentMileage={setCurrentMileage}
        onResetCarMileageModal={resetCarMileageModal}
        onHandleCarMileageConfirm={handleCarMileageConfirm}
        title={t("v8_missing_mileage", "Missing Mileage")}
      />

      <DeskCommunication appointment={appointment} showDeskComm={showDeskCommunicationModal} hideDeskButton onClose={() => setShowDeskCommunicationModal(false)} />
    </div>
  );
};
